import { useState } from 'react';
import Sheet from './Sheet';
import Icon from './Icon';
import { PRIMARY } from './CaptureSheets';
import { useApp } from '../lib/AppContext';
import { adPerRequest, watchAdForCredit } from '../lib/credits';
import { ProBadge } from './ProSheet';
import { useTrialOfferShown } from './Trial';

// "Bạn đã dùng hết lượt AI hôm nay": when the next credits come, Pro, and a way to carry on by hand (`manual`
// from the form that was refused). Once ads are on, free accounts need a rewarded ad for every AI request
// (in the app): the sheet plays it and then runs the refused request again (`retry`).
// The 7-day trial always comes first while the account can still try Pro (it starts here, then the refused request
// runs again); otherwise that place offers Pro. The ad and typing it in yourself stay below.
export default function OutOfCreditsSheet({ open, manual, message, retry, onClose, onUpgrade }) {
  const { api, membership, setMembership, native } = useApp();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const canTrial = open && !!membership?.trial_available;
  useTrialOfferShown('quota', canTrial);

  // Without starting the trial, closing the sheet is "Để sau" for the offer here.
  const close = () => {
    if (canTrial) api.post('/membership/prompted', { placement: 'quota' }).then(setMembership).catch(() => {});
    onClose();
  };

  const startTrial = async () => {
    setBusy(true);
    setNote(null);
    try {
      setMembership(await api.post('/membership/trial', { placement: 'quota' }));
      onClose();
      retry?.();
    } catch (e) {
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  };
  const credits = membership?.credits;
  const ads = membership?.ads;
  const perRequest = adPerRequest(membership);
  const adsLeft = perRequest && ads.left > 0;
  const canWatch = adsLeft && !!native?.showRewardedAd;

  const watch = async () => {
    setBusy(true);
    setNote(null);
    try {
      const next = await watchAdForCredit({ api, native, setMembership, membership });
      if (next.credits.left > 0) {
        close();
        retry?.();
      }
    } catch (e) {
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  };
  let title = message?.startsWith('Mỗi tuần') ? 'Đã đủ thực đơn tuần này' : 'Bạn đã dùng hết lượt AI';
  let text = message || 'Lượt mới sẽ có lúc 0:00 ngày mai.';
  if (adsLeft && !message?.includes('tháng')) {
    title = 'Xem quảng cáo để dùng AI';
    text = canWatch
      ? `Mỗi lượt AI cần xem một quảng cáo ngắn, hôm nay còn ${ads.left} lần. Gói Pro không có quảng cáo.`
      : 'Mỗi lượt AI cần xem một quảng cáo ngắn, chỉ có trong app Macro Mate trên điện thoại. Gói Pro dùng AI ở mọi nơi, không quảng cáo.';
  } else if (!membership?.pro && !canTrial) {
    text += ' Gói Pro có thêm lượt AI mỗi ngày cho chụp món ăn, gợi ý thực đơn và gợi ý mục tiêu.';
  }

  return (
    <Sheet open={open} onClose={close} title="" label={title}>
      {open && (
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="grid size-14 place-items-center rounded-full bg-[#fdebe4] text-[#c2410c]"><Icon name="sparkles" className="size-7" /></span>
          <div>
            <p className="text-lg font-bold">{title}</p>
            <p className="mt-1 text-sm text-muted">{text}</p>
          </div>
          {canTrial && (
            <div className="flex w-full flex-col gap-2 rounded-[16px] border-2 border-primary bg-[#e3f1e9]/50 p-4 text-left">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 font-bold"><ProBadge /> Dùng thử Pro 7 ngày</span>
                <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-extrabold uppercase text-primary-content">Miễn phí</span>
              </div>
              <p className="text-sm text-muted">
                20 lượt AI mỗi ngày, có ngay bây giờ. Không cần thanh toán, không tự gia hạn: hết 7 ngày tự về bản Free.
              </p>
              <button type="button" className={PRIMARY} disabled={busy} onClick={startTrial}>
                {busy && <span className="loading loading-spinner loading-sm" />} {retry ? 'Dùng thử ngay và tiếp tục' : 'Dùng thử ngay'}
              </button>
            </div>
          )}
          {!canTrial && !membership?.pro && (
            <button type="button" className={canWatch ? 'btn h-12 w-full rounded-2xl border-primary bg-base-100 font-bold text-brand-text' : PRIMARY}
              onClick={onUpgrade}>
              {canWatch ? 'Nâng cấp Pro, không quảng cáo' : 'Nâng cấp Pro'}
            </button>
          )}
          {credits?.limit != null && !perRequest && (
            <div className="flex w-full items-center justify-between rounded-[14px] bg-base-200 px-4 py-3 text-sm">
              <span>Lượt AI hôm nay</span>
              <b className="tabular">{credits.used}/{credits.limit} đã dùng</b>
            </div>
          )}
          {canWatch && (
            <div className="flex w-full flex-col items-center gap-1">
              <button type="button" className={canTrial ? 'btn h-12 w-full rounded-2xl border-line bg-base-100 font-semibold' : PRIMARY} disabled={busy} onClick={watch}>
                {busy ? <span className="loading loading-spinner loading-sm" /> : <Icon name="chevronRight" className="size-5" />} {retry ? 'Xem quảng cáo và tiếp tục' : 'Xem quảng cáo, nhận 1 lượt'}
              </button>
              <span className="text-xs text-muted">Khoảng 30 giây</span>
            </div>
          )}
          {note && <p role="status" className="text-sm text-muted">{note}</p>}
          {manual && (
            <button type="button" className="btn btn-ghost h-11 rounded-2xl font-semibold text-brand-text"
              onClick={() => { close(); manual.onClick(); }}>
              {manual.label}
            </button>
          )}
          {!manual && <button type="button" className="btn btn-ghost h-11 rounded-2xl" onClick={close}>Đóng</button>}
        </div>
      )}
    </Sheet>
  );
}
